
import { Component, Vue } from "vue-property-decorator";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTable from "@/components/vTable.vue";
import {downloadURI, mapNutritions} from "@/utils/Helpers";
import { Dish } from "@/models/Dishes/Dish";
import vModal from "@/components/vModal.vue";
import vRecipeListing from "@/components/vRecipeListing.vue";
import vRecipeDetails from "@/components/vRecipeDetails.vue";
import vAuditLogModal from "@/components/vAuditLogModal.vue";
import { ItemCompact } from "@/models/ItemCompact";
import { CaloriesCalculationMethod } from "@/models/Recipes/CaloriesCalculationMethod";
import { DailyValueCalculationMethod } from "@/models/Recipes/DailyValueCalculationMethod";
import vCopyRecipeModal from "@/components/vCopyRecipeModal.vue";
import vSingleNutrientReportModal from "@/components/reports/vSingleNutrientReportModal.vue";
import vProteinQualityReport from "@/components/reports/vProteinQualityReport.vue";
import vBreakdownReportModal from "@/components/vBreakdownReportModal.vue";
import VAminoAcidScoreReport from "@/components/reports/vAminoAcidScoreReport.vue";
import RecipesRepository from "@/utils/API/RecipesRepository";
import vRecipeDeleteConfirmationModal from "@/components/vRecipeDeleteConfirmationModal.vue";
import vMultiColumnReportModal from "@/components/vMultiColumnReportModal.vue";
import {ErrorResponse} from "@/utils/API/ResultTypes/ErrorResponse";
import {AccountState} from "@/store/modules/AccountModule";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import vPreLoader from "@/components/vPreLoader.vue";
const vSelectFx = () => import("@/components/vSelectFx.vue");

interface DishesState {
  dish: Dish;
  selectedDish: ItemCompact;
}

@Component({
  components: {
    VUpsellModal,
    vMultiColumnReportModal,
    vBreadcrumbs,
    vTable,
    vModal,
    vSelectFx,
    vRecipeListing,
    vRecipeDetails,
    vAuditLogModal,
    vCopyRecipeModal,
    vBreakdownReportModal,
    vSingleNutrientReportModal,
    vProteinQualityReport,
    VAminoAcidScoreReport,
    vRecipeDeleteConfirmationModal,
    vPreLoader,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class Recipes extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;

  dishesState: DishesState = {
    dish: this.createDefaultDish(),
    selectedDish: {
      id: 0,
      name: "",
      createdOn: null,
      updatedOn: null,
      createdUserName: "",
      updatedUserName: ""
    }
  };

  // Modal options
  showDeleteRecipeModal = false;
  showCopyRecipeModal = false;
  showBreakdownReportModal = false;
  showProteinQualityReportModal = false;
  showAminoAcidScoreReportModal = false;
  showSingleNutrientReportModal = false;
  showMultiColumnReportModal = false;

  recipeForCopy: Dish = this.createDefaultDish();
  checkedRecipesForDelete: number[] = [];
  funcHandler :any;

  // List of dish ids to be deleted
  removeList: number[] = [];
  checked: number[] = [];

  // Default selected export country
  // "0" - Canada
  // "1" - US
  // "2" - UK
  exportCountryType: any = "0";

  /**
   * Indicates whether the "Download Bulk Recipe Report" button is disabled.
   *
   * When set to `true`, the button is disabled and cannot be interacted with by the user.
   * When set to `false`, the button is enabled and available for user interaction.
   */
  isDownloadBulkRecipeReportButtonDisabled = false;

  showTrialUpsellModal: boolean = false;
  isDishDetailsLoading = false;
  lastDishDetailsRequestId = 0;

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
  }

  changeCountryForExport(id: any) {
    this.exportCountryType = id;
  }

  print() {
    window.print();
  }

  get isTrial() {
    return this.$store.getters.getUserDetails.isTrial;
  }

  openReport(id: number) {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    switch (id) {
    case 0:
      this.openRecipeReportModal();
      break;
    case 1:
      this.openProteinQualityReportModal();
      break;
    case 2:
      this.openAminoAcidScoreReportModal();
      break;
    case 3:
      this.openSingleNutrientReportModal();
      break;
    case 4:
      this.openMultiColumnReportModal();
      break;
    }
  }

  /**
   * Handles the process of downloading a report. If the user is on a trial account,
   * it triggers an upsell modal. Otherwise, it proceeds to download the report.
   *
   * @return {void} No return value. Executes operations based on user account state.
   */
  async handleReportDownload() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    let country = "";

    switch (this.exportCountryType) {
    case "0":
      country = "Canada";
      break;

    case "1":
      country = "USA";
      break;

    case "2":
      country = "UK";
      break;
    }

    this.isDownloadBulkRecipeReportButtonDisabled = true;
    await downloadURI(this.exportToExcelURL, `Combined recipes report (${country})`);
    this.isDownloadBulkRecipeReportButtonDisabled = false;
  }

  // Open modal and view recipe report
  openRecipeReportModal() {
    this.showBreakdownReportModal = true;
  }

  openProteinQualityReportModal() {
    this.showProteinQualityReportModal = true;
  }

  hideProteinQualityReportModal() {
    this.showProteinQualityReportModal = false;
  }

  openAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = true;
  }

  hideAminoAcidScoreReportModal() {
    this.showAminoAcidScoreReportModal = false;
  }

  openSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = true;
  }

  hideSingleNutrientReportModal() {
    this.showSingleNutrientReportModal = false;
  }

  openMultiColumnReportModal(){
    this.showMultiColumnReportModal = true;
  }

  hideMultiColumnReportModal(){
    this.showMultiColumnReportModal = false;
  }

  auditLogModal = {
    isOpen: false,
  };

  onAuditLogClick(dish: ItemCompact) {
    this.auditLogModal.isOpen = true;
    this.dishesState.selectedDish = { ...dish };
  }

  openCopyModal() {
    this.showCopyRecipeModal = true;
    RecipesRepository.getRecipeDetails(this.dishesState.dish.id, true).then(
      (response) => {
        this.recipeForCopy = response.data;
        this.recipeForCopy.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
      }
    );
  }

  openDeleteModal(recipeId: number[], handler: (recipeIds: number[]) => void): void {
    this.checkedRecipesForDelete = recipeId;
    this.showDeleteRecipeModal = true;
    this.funcHandler = handler;
  }

  createDefaultDish(): Dish {
    return {
      id: 0,
      name: '',
      description: '',
      nutritionAnalysis: null,
      createdOn: null,
      updatedOn: null,
      createdUserName: null,
      updatedUserName: null,

      servings: 1,
      recipeCategory: null,
      instructions: null,

      ingredients: [],
      childRecipes: [],
      caloriesCalculationMethod: CaloriesCalculationMethod.Summation,
      dailyValueCalculationMethod: DailyValueCalculationMethod.Rounded,
      proteinDvCalculationMethod: 0,
      pdcaasScore: 1,
      nutritionCountry: { id: 0, name: 'Canada' },
      referenceAmount: 0,
      recipeProductType: {
        id: 0,
        name: "",
        productType: 0,
        countryId: 0,
        createdOn: null
      },
      productTypeId: 0,
    };
  }

  /**
   * Dynamically return the url for export depending on the current environment
   */
  get exportToExcelURL() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Dishes/ExportDishes?country=${this.exportCountryType}`;
  }

  /**
   * Click handler for showing the selected recipe
   * */
  async chooseDish(dishId: number) {
    this.isDishDetailsLoading = true;
    const requestId = ++this.lastDishDetailsRequestId;

    try {
      const response = await RecipesRepository.getRecipeDetails(dishId, true);

      if (requestId !== this.lastDishDetailsRequestId) {
        return;
      }

      this.dishesState.dish = { ...(response.data as Dish) };
      this.dishesState.dish.nutritionAnalysis = mapNutritions(response.data.nutritionAnalysis);
    } catch (error: any) {
      if (requestId !== this.lastDishDetailsRequestId) {
        return;
      }

      if (error.response) {
        this.$snotify.error(error.response.data?.errorMessages?.[0]?.userVisibleMessage || error.response.data?.message);
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    } finally {
      if (requestId === this.lastDishDetailsRequestId) {
        this.isDishDetailsLoading = false;
      }
    }
  }

  get translations() {
    return this.localizationState.translations;
  }

  /**
   * Method which requests delete recipes to API and shows toastr message
   */
  removeRecipes() {
    this.funcHandler(this.checkedRecipesForDelete);
    this.showDeleteRecipeModal = false;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "dishes", "ingredients"],
      })
      .then(() => {
        document.title = (
          this.translations.get("dish_title") + " " + this.translations.get("meta_title_postfix")
        );
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  saveCopiedRecipe(newRecipeName : string) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("rec_loading"),
      () =>
        new Promise((success, reject) => {
          RecipesRepository.copyRecipe(this.recipeForCopy.id, newRecipeName)
            .then((response) => {
              this.showCopyRecipeModal = false;
              this.$router.push({
                name: "dishesEdit",
                params: { id: `${response.data.id}` },
              });
              success({
                body: this.translations.get("rec_copy_combined_recipe_success"),
              });
            })
            .catch((error: ErrorResponse) => {
              this.showCopyRecipeModal = false;
              let message;

              if (error.response) {
                message = error.response.data.message;
              }

              else {
                message = "Unknown error while copying item.";
              }

              reject({ body: message });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }
}
export default Recipes;
